import { LOCALES, type Locale, type User } from '@deskworks/shared-types';
import { useState, type FormEvent } from 'react';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { LOCALE_NAMES, locale, setLocale, t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** The signed-in person's name and language. A new language loads the page again in it. */
export function ProfileDialog({ user, open, onOpenChange }: { user: User; open: boolean; onOpenChange: (open: boolean) => void }) {
  const [name, setName] = useState(user.name);
  const [language, setLanguage] = useState<Locale>(user.locale ?? locale);

  const reset = (next: boolean) => {
    if (next) {
      setName(user.name);
      setLanguage(user.locale ?? locale);
    }
    onOpenChange(next);
  };

  const save = useApiMutation(() => api.auth.updateMe({ name: name.trim(), locale: language }), {
    invalidate: [qk.me],
    onSuccess: (saved) => {
      onOpenChange(false);
      if (saved.locale) setLocale(saved.locale);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('Profile')}</DialogTitle>
            <DialogDescription>{t('Your name and the language Deskworks shows.')}</DialogDescription>
          </DialogHeader>
          <Field label={t('Name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          </Field>
          <Field label={t('Email')}>
            <Input value={user.email} disabled />
          </Field>
          <Field label={t('Language')}>
            <Select value={language} onValueChange={(v) => setLanguage(v as Locale)}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCALES.map((l) => (
                  <SelectItem key={l} value={l} lang={l}>
                    {LOCALE_NAMES[l]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
